import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { MainLayout } from './components/layout/MainLayout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ClientiPage } from './pages/ClientiPage';
import { CommessePage } from './pages/CommessePage';
import { CommessaDetailPage } from './pages/CommessaDetailPage';
import { RisorsePage } from './pages/RisorsePage';
import { RuoliPage } from './pages/RuoliPage';
import { TimesheetPage } from './pages/TimesheetPage';
import { TimesheetApprovalPage } from './pages/TimesheetApprovalPage';
import { UserRolesPage } from './pages/UserRolesPage';
import { CostiEsterniPage } from './pages/CostiEsterniPage';
import { CostiApprovalPage } from './pages/CostiApprovalPage';
import { GestioneUtentiPage } from './pages/GestioneUtentiPage';
import { GestioneInvitiPage } from './pages/GestioneInvitiPage';
import { RicaviPage } from './pages/RicaviPage';
import { PermissionManagementPage } from './pages/PermissionManagementPage';
import { SegnalazioniPage } from './pages/SegnalazioniPage';
import { RegisterPage } from './pages/RegisterPage';
import './App.css';

// Protected Route wrapper with role check
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role-based access
  if (allowedRoles && !allowedRoles.includes(user?.ruolo)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Permission-based route wrapper (uses dynamic permissions)
const PermissionRoute = ({ children, module, action, fallback = null }) => {
  const { user } = useAuth();
  const { hasPermission, isLoading } = usePermissions();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Admin always has access
  if (user?.ruolo === 'admin') {
    return children;
  }
  
  if (!hasPermission(module, action)) {
    return fallback || <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Role-protected route wrapper (legacy)
const RoleRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();
  
  if (allowedRoles && !allowedRoles.includes(user?.ruolo)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirect to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      {/* Public registration route (with token) */}
      <Route path="/register" element={<RegisterPage />} />
      
      {/* Protected routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="clienti" element={
          <RoleRoute allowedRoles={['admin', 'coordinator']}>
            <ClientiPage />
          </RoleRoute>
        } />
        <Route path="commesse" element={
          <RoleRoute allowedRoles={['admin', 'coordinator', 'pm']}>
            <CommessePage />
          </RoleRoute>
        } />
        <Route path="commesse/:id" element={
          <RoleRoute allowedRoles={['admin', 'coordinator', 'pm']}>
            <CommessaDetailPage />
          </RoleRoute>
        } />
        <Route path="risorse" element={
          <RoleRoute allowedRoles={['admin', 'coordinator']}>
            <RisorsePage />
          </RoleRoute>
        } />
        <Route path="ruoli" element={
          <RoleRoute allowedRoles={['admin', 'coordinator']}>
            <RuoliPage />
          </RoleRoute>
        } />
        <Route path="timesheet" element={<TimesheetPage />} />
        <Route path="timesheet/approvazione" element={
          <RoleRoute allowedRoles={['admin', 'coordinator', 'pm']}>
            <TimesheetApprovalPage />
          </RoleRoute>
        } />
        <Route path="ruoli-utente" element={
          <RoleRoute allowedRoles={['admin']}>
            <UserRolesPage />
          </RoleRoute>
        } />
        <Route path="gestione-permessi" element={
          <RoleRoute allowedRoles={['admin']}>
            <PermissionManagementPage />
          </RoleRoute>
        } />
        <Route path="gestione-utenti" element={
          <RoleRoute allowedRoles={['admin']}>
            <GestioneUtentiPage />
          </RoleRoute>
        } />
        <Route path="gestione-inviti" element={
          <RoleRoute allowedRoles={['admin']}>
            <GestioneInvitiPage />
          </RoleRoute>
        } />
        <Route path="costi-esterni" element={
          <RoleRoute allowedRoles={['admin', 'coordinator', 'pm']}>
            <CostiEsterniPage />
          </RoleRoute>
        } />
        <Route path="costi-approvazione" element={
          <RoleRoute allowedRoles={['admin', 'coordinator', 'pm']}>
            <CostiApprovalPage />
          </RoleRoute>
        } />
        <Route path="ricavi" element={
          <RoleRoute allowedRoles={['admin', 'coordinator', 'pm']}>
            <RicaviPage />
          </RoleRoute>
        } />
        {/* Segnalazioni - Accessibile a tutti i ruoli */}
        <Route path="segnalazioni" element={<SegnalazioniPage />} />
      </Route>
      
      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PermissionsProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'hsl(var(--card))',
                  color: 'hsl(var(--card-foreground))',
                  border: '1px solid hsl(var(--border))',
                },
              }}
            />
          </BrowserRouter>
        </PermissionsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
